// src/theme/colors.js

// Original theme colors - preserved exactly as they were
const originalDarkThemeColors = {
  primary: {
    main: '#0D2E4D',
    light: '#1C4670',
    dark: '#061B32',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#f2c526',
    light: '#FFD859',
    dark: '#D9AD1F',
    contrastText: '#0D2E4D',
  },
  background: {
    default: '#071725',
    paper: '#0A243A',
    light: '#102E4A',
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#B8C9D9',
    disabled: '#627D96',
    accent: '#f2c526',
  },
  success: {
    main: '#4CAF50',
    light: '#81C784',
    dark: '#388E3C',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
    contrastText: '#000000',
  },
  error: {
    main: '#F44336',
    light: '#E57373',
    dark: '#D32F2F',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#2196F3',
    light: '#64B5F6',
    dark: '#1976D2',
    contrastText: '#FFFFFF',
  },
  action: {
    active: '#FFFFFF',
    hover: 'rgba(255, 255, 255, 0.08)',
    selected: 'rgba(255, 255, 255, 0.12)',
    disabled: 'rgba(255, 255, 255, 0.3)',
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
    focus: 'rgba(255, 255, 255, 0.12)',
  },
};

const originalLightThemeColors = {
  primary: {
    main: '#0F3A61',
    light: '#2B5885',
    dark: '#072747',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#f2c526',
    light: '#FFD859',
    dark: '#D9AD1F',
    contrastText: '#0F3A61',
  },
  background: {
    default: '#F8FAFD',
    paper: '#FFFFFF',
    light: '#EEF2F7',
  },
  text: {
    primary: '#0F3A61',
    secondary: '#4A6785',
    disabled: '#90A0B7',
    accent: '#D9AD1F',
  },
  success: {
    main: '#43A047',
    light: '#76D275',
    dark: '#2E7D32',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#FB8C00',
    light: '#FFAC42',
    dark: '#C56000',
    contrastText: '#000000',
  },
  error: {
    main: '#E53935',
    light: '#FF6B67',
    dark: '#AB000D',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#1E88E5',
    light: '#6AB7FF',
    dark: '#005CB2',
    contrastText: '#FFFFFF',
  },
  action: {
    active: 'rgba(0, 0, 0, 0.54)',
    hover: 'rgba(0, 0, 0, 0.04)',
    selected: 'rgba(0, 0, 0, 0.08)',
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
    focus: 'rgba(0, 0, 0, 0.12)',
  },
};

// Color Palette Definitions - Focused on visibility and eye comfort
export const colorPalettes = {
  // Original Theme - The exact default Aghrba theme
  original: {
    name: 'Original Theme',
    isDirect: true, // Flag to use direct colors
  },
  
  // Soft Blue - Easy on the eyes with good contrast
  softBlue: {
    name: 'Soft Blue',
    primary: '#2E5C8A',     // Softer medium blue
    secondary: '#f2c526',   // Golden accent
    tertiary: '#4A7BA7',    // Light blue
    background: '#0F1923', // Slightly lighter dark background
    backgroundPaper: '#162637', // Lighter paper
    accent: '#6CA4DC',      // Soft blue accent
  },
  
  // High Contrast - Maximum visibility
  highContrast: {
    name: 'High Contrast',
    primary: '#4A90E2',     // Bright blue for better visibility
    secondary: '#FFD700',   // Bright gold
    tertiary: '#64B5F6',    // Light blue
    background: '#000000',  // Pure black background
    backgroundPaper: '#1A1A1A', // Dark grey paper
    accent: '#00D4FF',      // Cyan accent
  },
  
  // Warm Night - Reduces blue light for evening use
  warmNight: {
    name: 'Warm Night',
    primary: '#8B6914',     // Warm brown-gold
    secondary: '#f2c526',   // Golden yellow
    tertiary: '#CD853F',    // Peru color
    background: '#1A1410',  // Warm dark background
    backgroundPaper: '#2A2118', // Warm paper
    accent: '#DDA15E',      // Warm accent
  },
  
  // Ocean Breeze - Calming with good readability
  oceanBreeze: {
    name: 'Ocean Breeze',
    primary: '#00ACC1',     // Cyan
    secondary: '#f2c526',   // Golden accent
    tertiary: '#4DD0E1',    // Light cyan
    background: '#0A1929',  // Deep ocean background
    backgroundPaper: '#122740', // Ocean paper
    accent: '#80DEEA',      // Light cyan accent
  },
};

// Function to generate theme colors based on palette
const generateThemeColors = (palette, mode = 'dark') => {
  // If it's the original theme, return the exact original colors
  if (palette.isDirect) {
    return mode === 'dark' ? originalDarkThemeColors : originalLightThemeColors;
  }
  
  const { primary, secondary, tertiary, background, backgroundPaper, accent } = palette;
  
  // Helper function to lighten color
  const lighten = (color, amount = 0.2) => {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * amount * 100);
    const R = ((num >> 16) + amt);
    const G = ((num >> 8 & 0x00FF) + amt);
    const B = ((num & 0x0000FF) + amt);
    return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  };
  
  // Helper function to darken color
  const darken = (color, amount = 0.2) => {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * amount * 100);
    const R = ((num >> 16) - amt);
    const G = ((num >> 8 & 0x00FF) - amt);
    const B = ((num & 0x0000FF) - amt);
    return '#' + (0x1000000 + (R > 0 ? R : 0) * 0x10000 +
      (G > 0 ? G : 0) * 0x100 +
      (B > 0 ? B : 0)).toString(16).slice(1);
  };
  
  if (mode === 'dark') {
    return {
      primary: {
        main: primary,
        light: tertiary || lighten(primary, 0.25),
        dark: darken(primary, 0.15),
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: secondary,
        light: lighten(secondary, 0.15),
        dark: darken(secondary, 0.15),
        contrastText: secondary === '#f2c526' || secondary === '#FFD700' ? '#0D2E4D' : '#FFFFFF',
      },
      background: {
        default: background,
        paper: backgroundPaper || lighten(background, 0.1),
        light: lighten(backgroundPaper || background, 0.15),
      },
      text: {
        primary: '#FFFFFF',
        secondary: lighten(accent, 0.4) || '#B8C9D9',
        disabled: lighten(background, 0.5),
        accent: accent || secondary,
      },
      // Status colors remain consistent
      success: {
        main: '#4CAF50',
        light: '#81C784',
        dark: '#388E3C',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#FF9800',
        light: '#FFB74D',
        dark: '#F57C00',
        contrastText: '#000000',
      },
      error: {
        main: '#F44336',
        light: '#E57373',
        dark: '#D32F2F',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#2196F3',
        light: '#64B5F6',
        dark: '#1976D2',
        contrastText: '#FFFFFF',
      },
      action: {
        active: '#FFFFFF',
        hover: 'rgba(255, 255, 255, 0.08)',
        selected: 'rgba(255, 255, 255, 0.12)',
        disabled: 'rgba(255, 255, 255, 0.3)',
        disabledBackground: 'rgba(255, 255, 255, 0.12)',
        focus: 'rgba(255, 255, 255, 0.12)',
      },
    };
  } else {
    // Light mode adjustments with better color handling
    // For light mode, we need to ensure good contrast
    let lightPrimary = primary;
    
    // Adjust colors that are too light for light mode
    if (primary === '#00ACC1' || primary === '#4A90E2') {
      lightPrimary = darken(primary, 0.3);
    } else if (primary === '#8B6914') {
      lightPrimary = darken(primary, 0.1);
    } else if (primary === '#2E5C8A') {
      lightPrimary = darken(primary, 0.2);
    }
    
    return {
      primary: {
        main: lightPrimary,
        light: lighten(lightPrimary, 0.2),
        dark: darken(lightPrimary, 0.1),
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: secondary === '#FFD700' ? '#D9AD1F' : secondary,
        light: lighten(secondary, 0.15),
        dark: darken(secondary, 0.15),
        contrastText: lightPrimary,
      },
      background: {
        default: '#F8FAFD',
        paper: '#FFFFFF',
        light: '#EEF2F7',
      },
      text: {
        primary: lightPrimary,
        secondary: lighten(lightPrimary, 0.3),
        disabled: '#90A0B7',
        accent: darken(accent || secondary, 0.2),
      },
      // Status colors for light mode
      success: {
        main: '#43A047',
        light: '#76D275',
        dark: '#2E7D32',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#FB8C00',
        light: '#FFAC42',
        dark: '#C56000',
        contrastText: '#000000',
      },
      error: {
        main: '#E53935',
        light: '#FF6B67',
        dark: '#AB000D',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#1E88E5',
        light: '#6AB7FF',
        dark: '#005CB2',
        contrastText: '#FFFFFF',
      },
      action: {
        active: 'rgba(0, 0, 0, 0.54)',
        hover: 'rgba(0, 0, 0, 0.04)',
        selected: 'rgba(0, 0, 0, 0.08)',
        disabled: 'rgba(0, 0, 0, 0.26)',
        disabledBackground: 'rgba(0, 0, 0, 0.12)',
        focus: 'rgba(0, 0, 0, 0.12)',
      },
    };
  }
};

// Export default colors for backward compatibility
export const darkThemeColors = originalDarkThemeColors;
export const lightThemeColors = originalLightThemeColors;

// Export the function for theme generation
export { generateThemeColors };

// Color tokens for consistent usage
export const colorTokens = {
  gradients: {
    goldText: (colors) => `linear-gradient(90deg, ${colors.secondary.light} 0%, ${colors.secondary.main} 100%)`,
    primaryButton: (colors) => `linear-gradient(45deg, ${colors.primary.dark} 0%, ${colors.primary.main} 100%)`,
    secondaryButton: (colors) => `linear-gradient(45deg, ${colors.secondary.dark} 0%, ${colors.secondary.main} 100%)`,
    cardBorder: (colors) => `linear-gradient(135deg, ${colors.secondary.main}, ${colors.secondary.light}, ${colors.secondary.main})`,
    pageBackground: (colors) => `linear-gradient(135deg, ${colors.background.default} 0%, ${colors.background.paper} 100%)`,
  },
  shadows: {
    cardShadow: (mode) => mode === 'dark' 
      ? '0 8px 16px rgba(0, 0, 0, 0.2)' 
      : '0 8px 16px rgba(0, 0, 0, 0.08)',
    buttonShadow: (mode, color) => mode === 'dark' 
      ? `0 4px 10px ${color}30` 
      : `0 4px 10px ${color}20`,
  },
  effects: {
    glow: (color, mode) => mode === 'dark'
      ? `0 0 10px ${color}40, 0 0 30px ${color}20`
      : `0 0 10px ${color}30, 0 0 30px ${color}10`,
    inputFocus: (color) => `0 0 0 3px ${color}40`,
  },
};